import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { profileErrorCode } from './spotifyUploadState';

type BioFacts = Pick<ArtistProfileResponse, 'bio' | 'spotify'>;

/**
 * True when "Upload bio to Spotify" shows: the workspace has Auto upload access and a long bio is
 * saved. With no access the bio tool shows the steps to paste it by hand ("Where it goes"). Pure.
 */
export function spotifyBioButtonShown(profile: BioFacts): boolean {
  return profile.spotify.bio.unavailable !== 'noAutoUploadAccess' && profile.bio.bioLong.trim() !== '';
}

/** What the bio sheet shows before Send. */
export type SpotifyBioSheetView =
  | { kind: 'manual' }
  | { kind: 'notConnected' }
  | { kind: 'reconnect' }
  | { kind: 'nothing' }
  | { kind: 'send' };

/** The view of the bio sheet from the profile. Pure. */
export function spotifyBioSheetView(profile: BioFacts): SpotifyBioSheetView {
  const { unavailable } = profile.spotify.bio;
  if (unavailable === 'noAutoUploadAccess') {
    return { kind: 'manual' };
  }
  if (unavailable === 'notConnected') {
    return { kind: 'notConnected' };
  }
  if (unavailable === 'reconnect') {
    return { kind: 'reconnect' };
  }
  return profile.bio.bioLong.trim() === '' ? { kind: 'nothing' } : { kind: 'send' };
}

/** The upload state of the saved bio: never sent, sent and the same, or changed after the upload. Pure. */
export type SpotifyBioUploadState =
  | { kind: 'none' }
  | { kind: 'onSpotify'; at: string }
  | { kind: 'changed'; at: string };

export function spotifyBioUploadState(profile: Pick<ArtistProfileResponse, 'spotify'>): SpotifyBioUploadState {
  const { sentAt, sentIsCurrent } = profile.spotify.bio;
  if (sentAt === null) {
    return { kind: 'none' };
  }
  return sentIsCurrent ? { kind: 'onSpotify', at: sentAt } : { kind: 'changed', at: sentAt };
}

/** What the sheet does after a refused Send. */
export type SpotifyBioPostOutcome =
  /** 403 M24_AUTO_UPLOAD_NOT_ENABLED: the steps take the place of Send. */
  | { kind: 'manual' }
  /** 409 M24_PUBLISH_NOT_CONNECTED (also `reconnect`): the profile reads again and shows Connect. */
  | { kind: 'notConnected' }
  /** 409 M24_PROFILE_RIGHTS_REQUIRED: the rights line must be ticked. */
  | { kind: 'rights' }
  /** 409 M24_PROFILE_NOTHING_READY: no long bio is saved. */
  | { kind: 'nothing' }
  /** 400 M24_PROFILE_BIO_TOO_LONG. */
  | { kind: 'tooLong' }
  /** 403 M24_PROFILE_SPOTIFY_NO_PERMISSION. */
  | { kind: 'noPermission' }
  /** 422 M24_PROFILE_SPOTIFY_REFUSED with the text of Spotify. */
  | { kind: 'refused'; detail: string | null }
  /** 502 M24_PROFILE_SPOTIFY_FAILED. */
  | { kind: 'failed' }
  | { kind: 'generic' };

function detailOf(error: unknown): string | null {
  const record = error as { m24Limit?: { detail?: unknown }; cause?: { detail?: unknown } } | null;
  const detail = record?.m24Limit?.detail ?? record?.cause?.detail;
  return typeof detail === 'string' && detail.trim() !== '' ? detail : null;
}

/** The outcome of a refused `POST .../artist-profile/spotify/bio`. Pure. */
export function spotifyBioPostOutcome(error: unknown): SpotifyBioPostOutcome {
  switch (profileErrorCode(error)) {
    case 'M24_AUTO_UPLOAD_NOT_ENABLED':
      return { kind: 'manual' };
    case 'M24_PUBLISH_NOT_CONNECTED':
      return { kind: 'notConnected' };
    case 'M24_PROFILE_RIGHTS_REQUIRED':
      return { kind: 'rights' };
    case 'M24_PROFILE_NOTHING_READY':
      return { kind: 'nothing' };
    case 'M24_PROFILE_BIO_TOO_LONG':
      return { kind: 'tooLong' };
    case 'M24_PROFILE_SPOTIFY_NO_PERMISSION':
      return { kind: 'noPermission' };
    case 'M24_PROFILE_SPOTIFY_REFUSED':
      return { kind: 'refused', detail: detailOf(error) };
    case 'M24_PROFILE_SPOTIFY_FAILED':
      return { kind: 'failed' };
    case null:
      return { kind: 'generic' };
    default:
      return { kind: 'generic' };
  }
}
